import React, { useCallback, useEffect, useRef, useState } from "react";

import SearchIcon from "@assets/icons/search.svg";
import PlusIcon from "@assets/icons/plus.svg";
import XCircleIcon from "@assets/icons/x-circle.svg";

import { arraysEqual } from "@utils/array.util";
import { cn } from "@utils/tailwind.util";
import { IBaseTheme, IThemeOption } from "@interfaces/sourcing.interface";
import { Input } from "@ui/FormElements";
import { Button } from "@ui/Button";
import { View } from "@ui/View";
import { IOptions, ThemeTree } from "./ThemeTree";

const SELECTED_BRANCHES_MAX_LIMIT = 5; // identifies how many branches from theme tree can be selected

interface IThemeTreeSelectProps {
  initialValues: string[];
  options: IOptions;
  selectedBranches: IBaseTheme[];
  onSelect: (theme: IThemeOption) => void;
  isMultipleSelect?: boolean;
  loadingThemeIds?: Set<string>;
  isLevelOneBased?: boolean;
  triggerClassName?: string;
}

export const ThemeTreeSelect = ({
  options,
  initialValues = [],
  selectedBranches,
  onSelect,
  isMultipleSelect = false,
  loadingThemeIds = new Set(),
  isLevelOneBased = false,
  triggerClassName,
}: IThemeTreeSelectProps) => {
  const [selected, setSelected] = useState(new Map(initialValues.map((id) => [id, 1])));
  const [searchQuery, setSearchQuery] = useState<string | undefined>("");
  const [isThemeDropdownOpen, setIsThemeDropdownOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const isMaxLimitReached = (selectedBranches?.length ?? 0) >= SELECTED_BRANCHES_MAX_LIMIT;

  const toggleSelection = useCallback(
    (item: IThemeOption) => {
      setSelected((prev) => {
        const newMap = new Map(prev);
        if (!newMap.has(item.id)) {
          newMap.set(item.id, 1);
        }

        // postpone onSelect, to prevent unnecessary re-renders
        setTimeout(() => onSelect(item), 0);

        return newMap;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSelect]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      !(event.target as HTMLElement).closest(".dropdown-item")
    ) {
      setIsThemeDropdownOpen(false);
      setSearchQuery("");
    }
  }, []);

  const filterOptions = useCallback((items: IOptions, query: string | undefined): IThemeOption[] => {
    return items
      .map((item) => {
        const filteredChildren = filterOptions(item.children, query);

        if (item.title.toLowerCase().includes(query || "") || filteredChildren.length > 0) {
          return { ...item, children: filteredChildren };
        }

        return undefined;
      })
      .filter((item): item is IThemeOption => item !== undefined);
  }, []);

  // update only if the expected ids are not equal to the current ids
  useEffect(() => {
    const expectedIds = Array.from(new Set(initialValues));
    const currentIds = Array.from(selected.keys());

    if (!arraysEqual(expectedIds, currentIds)) {
      setSelected(new Map(initialValues.map((id) => [id, 1])));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    if (searchQuery === undefined) {
      setSearchQuery("");
    }
  }, [searchQuery]);

  const hasThemes = initialValues?.length > 0;

  return (
    <div ref={dropdownRef} className="inline-block">
      <View.Condition if={isMultipleSelect || !hasThemes}>
        <Button
          className={cn(
            "text-gray-450 transition-all duration-200 ease-in hover:enabled:bg-action-secondaryHover focus:enabled:shadow-none active:enabled:shadow-none dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-invertedPrimary80 dark:focus:enabled:bg-gray-600",
            {
              "max-h-5 border-0 p-0": hasThemes,
              "w-full justify-start bg-[#F9F9FB]": !hasThemes,
            },
            triggerClassName
          )}
          buttonSize={hasThemes ? "SMALL" : "MEDIUM"}
          buttonType={"TRANSPARENT"}
          PrefixIcon={PlusIcon}
          strokeWidthIcon={2}
          onClick={() => setIsThemeDropdownOpen((prev) => !prev)}
        >
          {hasThemes ? "" : "Tag Sourcing Themes"}
        </Button>
      </View.Condition>
      {isThemeDropdownOpen && (
        <div className="absolute left-0 z-50 mt-2 max-h-[420px] max-w-full w-full rounded-lg border dark:border-gray-600 bg-white dark:bg-background-primaryDark shadow-md">
          <Input
            autoFocus
            isFullWidth={true}
            placeholder="Search sourcing themes"
            StartIcon={<SearchIcon color="#9CA1AF" width={20} height={20} />}
            EndIcon={
              <View.Condition if={!!searchQuery}>
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-5 w-5 cursor-pointer" onClick={() => setSearchQuery(undefined)} />
                </div>
              </View.Condition>
            }
            onChange={handleSearchChange}
            value={searchQuery || ""}
            className="m-1 pr-8"
          />

          <ThemeTree
            options={filterOptions(options, searchQuery)}
            selected={selected}
            selectedBranches={selectedBranches}
            onSelect={toggleSelection}
            selectedIds={initialValues}
            expandAll={!!searchQuery}
            searchQuery={searchQuery || undefined}
            loadingThemeIds={loadingThemeIds}
            isLevelOneBased={isLevelOneBased}
            isMaxLimitReached={isMaxLimitReached}
            className="border-t dark:border-gray-600 p-1"
          />
        </div>
      )}
    </div>
  );
};
